import { useMemo, useEffect } from 'react'

import { TitleDocument } from '@/pages/DocumentPage/components/TitleDocument'
import { OperationDocument } from '@/pages/DocumentPage/components/OperationDocument'
import { BillOfMaterialsTable } from '@/pages/DocumentPage/components/OperationDocument/BillOfMaterialsTable'
import { ToolsBillOfMaterialsTable } from '@/pages/DocumentPage/components/OperationDocument/ToolsBillOfMaterialsTable'
import { Spinner } from '@/components/ui/spinner'

import { useTechnicianDocument } from '../hooks/useTechnicianDocument'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useListPageViews } from '@/services/queries/views'

export const DocumentPage = () => {
  const { isLoading, data } = useDocumentPageQuery()
  const { currentPage } = useTechnicianDocument()

  const { isLoading: isLoadingViews, data: viewsData } = useListPageViews({
    cadVersionId: data?.version?.id,
    documentVersionId: data?.documentVersion?.id,
  })

  const views = useMemo(
    () =>
      (viewsData || [])
        .filter((view) => view.document_page === currentPage.id)
        .sort((viewA, viewB) => {
          return (
            (viewA.document_page_order || 0) - (viewB.document_page_order || 0)
          )
        }),
    [viewsData, currentPage.id],
  )

  const orderNumberMap = new Map()
  const getPageOrderNumber = (page: any): string => {
    if (page.type) return ''
    const orderNumber = page.order_number
    const pageParent = page.parent
      ? data?.documentPages?.find((p) => p.id === page.parent)
      : null
    if (!pageParent) {
      orderNumberMap.set(page.id, `${orderNumber}`)
      return `${orderNumber}`
    }

    const parentOrderNumber =
      orderNumberMap.get(pageParent.id) || getPageOrderNumber(pageParent)
    return `${parentOrderNumber}.${orderNumber}`
  }

  useEffect(() => {
    const isOperationPage = !currentPage.type
    if (
      !isLoading &&
      !isLoadingViews &&
      data &&
      isOperationPage &&
      currentPage &&
      Array.isArray(currentPage.template_values?.subPages) &&
      currentPage.subPageId
    ) {
      const subPages = currentPage.template_values.subPages as string[]
      const hiddenSubPages =
        (currentPage.template_values.hiddenSubPages as string[]) || []
      const visibleSubPages = subPages.filter(
        (subPage) => !hiddenSubPages.includes(subPage),
      )
      const index = visibleSubPages.findIndex(
        (p) => p === currentPage.subPageId,
      )

      const el = document.getElementById(`sub-page-index-${index}`)

      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [currentPage, data, isLoading, isLoadingViews])

  if (isLoading || isLoadingViews) {
    return (
      <div className="relative bg-white flex flex-col justify-between h-[612px] w-full">
        <Spinner />
      </div>
    )
  }

  if (!data) {
    return null
  }

  const versionNumber = data.documentVersion?.version_number || 1
  const versionName = data.documentVersion?.name || `V${versionNumber}`
  const createdAt = data.documentVersion?.created || new Date().toISOString()
  const isLatestDocumentVersion = false
  const documentName = data.doc?.name || 'Work Instructions'

  if (currentPage.type === 'title') {
    return (
      <TitleDocument
        parentRef={null}
        documentName={documentName}
        versionName={versionName}
        createdAt={createdAt}
        isLatestDocumentVersion={isLatestDocumentVersion}
      />
    )
  }

  if (currentPage.type === 'parts-bom') {
    return (
      <BillOfMaterialsTable
        rows={currentPage.rows || ([] as any)}
        versionName={versionName}
        createdAt={createdAt}
        isLatestDocumentVersion={isLatestDocumentVersion}
      />
    )
  }

  if (currentPage.type === 'tools-bom') {
    return (
      <ToolsBillOfMaterialsTable
        rows={currentPage.rows || ([] as any)}
        versionName={versionName}
        createdAt={createdAt}
        isLatestDocumentVersion={isLatestDocumentVersion}
      />
    )
  }

  if (currentPage) {
    return (
      <OperationDocument
        key={currentPage.id}
        documentPage={currentPage as any}
        views={views}
        versionName={versionName}
        orderNumber={getPageOrderNumber(currentPage as any) as any}
        createdAt={currentPage.created || createdAt}
        isReadOnly={true}
        isLatestDocumentVersion={isLatestDocumentVersion}
        hideCommentsButton
      />
    )
  }

  return null
}
